import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import toast from 'react-hot-toast';
import { Calendar, Check, X, Plus } from 'lucide-react';

export default function Leave() {
  const { user, isAdmin, hasPermission } = useAuth();
  const [tab, setTab] = useState(hasPermission('leave.approve') ? 'pending' : 'my');
  const [leaves, setLeaves] = useState([]);
  const [showApply, setShowApply] = useState(false);
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState({ casual: 12, sick: 6, personal: 3, used: 0 });

  const fetch = () => {
    const url = tab === 'pending' ? '/leave/pending' : tab === 'all' ? '/leave/all' : '/leave/my';
    api.get(url).then(r => { setLeaves(r.data.leaves); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { setLoading(true); fetch(); }, [tab]);

  // Calculate leave balance from approved leaves
  useEffect(() => {
    api.get('/leave/my').then(r => {
      const approved = r.data.leaves.filter(l => l.status === 'approved');
      const year = new Date().getFullYear();
      const thisYear = approved.filter(l => new Date(l.start_date).getFullYear() === year);
      const used = thisYear.reduce((sum, l) => {
        const days = Math.ceil((new Date(l.end_date) - new Date(l.start_date)) / (1000 * 60 * 60 * 24)) + 1;
        return sum + days;
      }, 0);
      const casual = thisYear.filter(l => l.leave_type === 'casual').reduce((s, l) => s + Math.ceil((new Date(l.end_date) - new Date(l.start_date)) / 86400000) + 1, 0);
      const sick = thisYear.filter(l => l.leave_type === 'sick').reduce((s, l) => s + Math.ceil((new Date(l.end_date) - new Date(l.start_date)) / 86400000) + 1, 0);
      const personal = thisYear.filter(l => l.leave_type === 'personal').reduce((s, l) => s + Math.ceil((new Date(l.end_date) - new Date(l.start_date)) / 86400000) + 1, 0);
      setBalance({ casual: Math.max(0, 12 - casual), sick: Math.max(0, 6 - sick), personal: Math.max(0, 3 - personal), used });
    }).catch(() => {});
  }, []);

  const approve = async (id) => { try { await api.put(`/leave/${id}/approve`); toast.success('Approved'); fetch(); } catch (e) { toast.error(e.response?.data?.error || 'Failed'); } };
  const deny = async (id) => { try { await api.put(`/leave/${id}/deny`); toast.success('Denied'); fetch(); } catch (e) { toast.error(e.response?.data?.error || 'Failed'); } };

  const STATUS_COLORS = { pending: 'bg-amber-100 text-amber-700', approved: 'bg-green-100 text-green-700', denied: 'bg-red-100 text-red-700' };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Leave Management</h1>
        <button onClick={() => setShowApply(true)} className="flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm"><Plus size={16} /> Apply Leave</button>
      </div>

      {/* Leave Balance */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border p-4 text-center"><p className="text-xs text-gray-500">Casual Left</p><p className="text-2xl font-bold text-blue-600">{balance.casual}</p><p className="text-[10px] text-gray-400">of 12 days</p></div>
        <div className="bg-white rounded-xl border p-4 text-center"><p className="text-xs text-gray-500">Sick Left</p><p className="text-2xl font-bold text-green-600">{balance.sick}</p><p className="text-[10px] text-gray-400">of 6 days</p></div>
        <div className="bg-white rounded-xl border p-4 text-center"><p className="text-xs text-gray-500">Personal Left</p><p className="text-2xl font-bold text-purple-600">{balance.personal}</p><p className="text-[10px] text-gray-400">of 3 days</p></div>
        <div className="bg-white rounded-xl border p-4 text-center"><p className="text-xs text-gray-500">Total Used</p><p className="text-2xl font-bold text-amber-600">{balance.used}</p><p className="text-[10px] text-gray-400">this year</p></div>
      </div>

      <div className="flex gap-1 border-b pb-px">
        {hasPermission('leave.approve') && <button onClick={() => setTab('pending')} className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === 'pending' ? 'border-brand-600 text-brand-600' : 'border-transparent text-gray-500'}`}>Pending</button>}
        <button onClick={() => setTab('my')} className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === 'my' ? 'border-brand-600 text-brand-600' : 'border-transparent text-gray-500'}`}>My Leaves</button>
        {isAdmin && <button onClick={() => setTab('all')} className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === 'all' ? 'border-brand-600 text-brand-600' : 'border-transparent text-gray-500'}`}>All</button>}
      </div>

      {loading ? <div className="flex justify-center py-10"><div className="animate-spin h-8 w-8 border-4 border-brand-600 border-t-transparent rounded-full" /></div> : (
        <div className="space-y-3">
          {leaves.map(l => (
            <div key={l.id} className="bg-white rounded-xl border p-4 flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  {(tab !== 'my') && <span className="font-medium text-sm">{l.requester?.name || user.name}</span>}
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[l.status]}`}>{l.status}</span>
                  <span className="text-[10px] bg-gray-100 px-2 py-0.5 rounded capitalize">{l.leave_type}</span>
                </div>
                <p className="text-sm text-gray-600 flex items-center gap-2"><Calendar size={14} /> {l.start_date} → {l.end_date}</p>
                {l.reason && <p className="text-xs text-gray-400 mt-1">{l.reason}</p>}
                {l.approver && <p className="text-xs text-gray-400 mt-1">{l.status === 'approved' ? 'Approved' : 'Denied'} by {l.approver.name}</p>}
              </div>
              {tab === 'pending' && l.status === 'pending' && hasPermission('leave.approve') && (
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => approve(l.id)} className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs"><Check size={14} /> Approve</button>
                  <button onClick={() => deny(l.id)} className="flex items-center gap-1 px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs"><X size={14} /> Deny</button>
                </div>
              )}
            </div>
          ))}
          {leaves.length === 0 && <p className="text-center text-gray-400 text-sm py-10 bg-white rounded-xl border">No leave requests</p>}
        </div>
      )}

      {showApply && <ApplyLeaveModal onClose={() => setShowApply(false)} onCreated={() => { setShowApply(false); fetch(); }} />}
    </div>
  );
}

function ApplyLeaveModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ leave_type: 'casual', start_date: '', end_date: '', reason: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post('/leave', form); toast.success('Leave applied'); onCreated(); } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="px-6 py-4 border-b flex items-center justify-between"><h2 className="text-lg font-semibold">Apply for Leave</h2><button onClick={onClose} className="text-gray-400 text-xl">&times;</button></div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <select value={form.leave_type} onChange={e => setForm(f => ({ ...f, leave_type: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm">
            {['casual', 'sick', 'personal', 'holiday'].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-3">
            <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} required className="px-3 py-2 border rounded-lg text-sm" />
            <input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} required className="px-3 py-2 border rounded-lg text-sm" />
          </div>
          <textarea value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} placeholder="Reason" rows={2} className="w-full px-3 py-2 border rounded-lg text-sm" />
          <div className="flex gap-3"><button type="button" onClick={onClose} className="flex-1 py-2 border rounded-lg text-sm text-gray-600">Cancel</button><button type="submit" disabled={saving} className="flex-1 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">{saving ? 'Applying...' : 'Apply'}</button></div>
        </form>
      </div>
    </div>
  );
}
